import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserDevices } from '../../../hooks/useUserDevices';
import { useAuthUser } from '../../../providers/AuthProvider';
import { useWorkspaceExport } from '../../research/packages/spectral-workspace/hooks/useWorkspaceExport';
import { useWorkspaceSessions } from '../../research/packages/spectral-workspace/hooks/useWorkspaceSessions';
import { createDefaultWorkspaceSettings } from '../../research/packages/spectral-workspace/utils/buildWorkspaceSnapshot';
import { buildSensorDeviceLookup } from '../../research/packages/spectral-workspace/utils/sessionDevice';
import type { SessionGroup } from '../types';
import {
  buildGroupHydrateEntries,
  buildGroupHydrateKey,
} from '../utils/buildGroupHydrateEntries';

const workspaceDefaults = createDefaultWorkspaceSettings();

export type UseSessionGroupWorkspaceExportParams = {
  group: SessionGroup | null | undefined;
  needsRepairSessionIds?: number[];
};

export const useSessionGroupWorkspaceExport = ({
  group,
  needsRepairSessionIds = [],
}: UseSessionGroupWorkspaceExportParams) => {
  const queryClient = useQueryClient();
  const user = useAuthUser();
  const { devices: userDevices } = useUserDevices(user?.id?.toString());
  const sensorDeviceLookup = useMemo(
    () => buildSensorDeviceLookup(userDevices),
    [userDevices]
  );

  const {
    sessionList,
    readySessions,
    isLoading,
    hydrateSessions,
    parsePendingSessionEvents,
  } = useWorkspaceSessions(sensorDeviceLookup);

  const [isPreparingSessions, setIsPreparingSessions] = useState(false);
  const lastHydratedKeyRef = useRef<string | null>(null);

  const sessionIds = group?.sessionIds ?? [];
  const canExport = sessionIds.length > 0;

  const meta = useMemo(
    () => ({
      name: group?.name ?? 'Gruppo sessioni',
      description: group?.description,
      author: user?.email || user?.name || 'Utente',
      createdAt: group?.createdAt ?? new Date().toISOString(),
    }),
    [group?.createdAt, group?.description, group?.name, user?.email, user?.name]
  );

  const selectedSpectraSessionId = useMemo(() => {
    const firstReady = readySessions[0];
    return firstReady ? String(firstReady.sessionId) : null;
  }, [readySessions]);

  const {
    opened,
    open: openExportModal,
    close,
    exportingId,
    isPreparingExportData,
    handleExportSingle,
    excelExports,
  } = useWorkspaceExport({
    meta,
    sessionList,
    readySessions,
    isLoadingSessions: isLoading || isPreparingSessions,
    customSegments: [],
    trendStart: workspaceDefaults.trendStart,
    trendEnd: workspaceDefaults.trendEnd,
    // One column per ready session (`Sessione {id}`), same wide layout as Research
    // when overlay is on with an empty selection (= all sessions).
    showOverlaySessions: true,
    selectedOverlaySessionIds: [],
    selectedSpectraSessionId,
    spectraStart: workspaceDefaults.spectraStart,
    spectraEnd: workspaceDefaults.spectraEnd,
    spectraLogScale: workspaceDefaults.spectraLogScale,
  });

  const hydrateGroupSessions = useCallback(
    (options: { force?: boolean } = {}) => {
      if (!group) {
        lastHydratedKeyRef.current = null;
        hydrateSessions([], { bulkLoad: false });
        return;
      }

      const hydrateKey = buildGroupHydrateKey(group.groupId, group.sessionIds);
      const hasUnresolvedEventParse = sessionList.some(
        session =>
          session.status === 'ready' &&
          session.hasHrData !== false &&
          session.events == null
      );

      if (
        !options.force &&
        !hasUnresolvedEventParse &&
        lastHydratedKeyRef.current === hydrateKey
      ) {
        parsePendingSessionEvents();
        return;
      }

      setIsPreparingSessions(true);

      try {
        lastHydratedKeyRef.current = hydrateKey;
        const { entries, needsFetch } = buildGroupHydrateEntries({
          queryClient,
          sessionIds: group.sessionIds,
          sensorDeviceLookup,
        });

        // Export needs HR events immediately: do not defer fetch/parse the way
        // the analysis page does for chart idle work (that left the modal stuck
        // on "Preparazione…" when pending parse was cancelled/skipped).
        hydrateSessions(entries, {
          bulkLoad: needsFetch,
          deferFetch: false,
          deferEventParse: false,
        });
        parsePendingSessionEvents();
      } catch {
        lastHydratedKeyRef.current = null;
        hydrateSessions([], { bulkLoad: false });
      } finally {
        setIsPreparingSessions(false);
      }
    },
    [
      group,
      hydrateSessions,
      parsePendingSessionEvents,
      queryClient,
      sensorDeviceLookup,
      sessionList,
    ]
  );

  useEffect(() => {
    lastHydratedKeyRef.current = null;
  }, [group?.groupId]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    parsePendingSessionEvents();
  }, [opened, parsePendingSessionEvents]);

  const open = useCallback(() => {
    if (!canExport) {
      notifications.show({
        color: 'yellow',
        title: 'Nessuna sessione',
        message: 'Aggiungi almeno una sessione al gruppo per esportare.',
      });
      return;
    }

    if (needsRepairSessionIds.length > 0) {
      notifications.show({
        color: 'yellow',
        title: 'Dati HR non riparati',
        message: `${needsRepairSessionIds.length} sessioni potrebbero avere dati HR non riparati. L’export userà i dati disponibili.`,
      });
    }

    hydrateGroupSessions({ force: true });
    openExportModal();
  }, [
    canExport,
    hydrateGroupSessions,
    needsRepairSessionIds,
    openExportModal,
  ]);

  return {
    canExport,
    opened,
    open,
    close,
    exportingId,
    isPreparingExportData,
    handleExportSingle,
    excelExports,
  };
};
